import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { phoneValidationService } from '@/services/features/PhoneValidationService';

interface PhoneValidationState {
  [phoneNumber: string]: boolean; // true = valid, false = invalid
}

export const usePhoneValidation = () => {
  const { hasuraToken } = useAuth();
  const [validationState, setValidationState] = useState<PhoneValidationState>({});
  const [isLoading, setIsLoading] = useState(false);

  const validatePhoneNumbers = useCallback(async (phoneNumbers: string[]) => {
    if (!hasuraToken || phoneNumbers.length === 0) {
      return;
    }

    // Filter valid phone numbers
    const validNumbers = phoneNumbers.filter(num => 
      num && num.trim() !== '' && num !== 'n/a' && num !== 'N/A'
    );

    if (validNumbers.length === 0) {
      return;
    }

    setIsLoading(true);

    try {
      const invalidNumbers = await phoneValidationService.getInvalidPhoneNumbers(validNumbers, hasuraToken);
      
      // Create validation state object
      const newValidationState: PhoneValidationState = {};
      
      validNumbers.forEach(num => {
        const cleanNum = phoneValidationService.cleanPhoneNumber(num);
        newValidationState[num] = !invalidNumbers.includes(cleanNum);
        // Also store by clean number for lookup
        newValidationState[cleanNum] = !invalidNumbers.includes(cleanNum);
      });

      setValidationState(prev => ({
        ...prev,
        ...newValidationState
      }));
    } catch (error) {
      console.error('Error validating phone numbers:', error);
      // Default to valid on error
      const defaultState: PhoneValidationState = {};
      validNumbers.forEach(num => {
        defaultState[num] = true;
        defaultState[phoneValidationService.cleanPhoneNumber(num)] = true;
      });
      setValidationState(prev => ({
        ...prev,
        ...defaultState
      }));
    } finally {
      setIsLoading(false);
    }
  }, [hasuraToken]);

  const isPhoneValid = useCallback((phoneNumber: string): boolean => {
    if (!phoneNumber || phoneNumber === 'n/a' || phoneNumber === 'N/A' || phoneNumber.trim() === '') {
      return false;
    }

    const cleanNum = phoneValidationService.cleanPhoneNumber(phoneNumber);
    
    // Check both original and clean number
    return validationState[phoneNumber] !== false && validationState[cleanNum] !== false;
  }, [validationState]);

  const isPhoneInvalid = useCallback((phoneNumber: string): boolean => {
    if (!phoneNumber || phoneNumber === 'n/a' || phoneNumber === 'N/A' || phoneNumber.trim() === '') {
      return false; // Don't show invalid for empty numbers
    }

    const cleanNum = phoneValidationService.cleanPhoneNumber(phoneNumber);
    
    // Explicitly check if marked as invalid
    return validationState[phoneNumber] === false || validationState[cleanNum] === false;
  }, [validationState]);

  const formatPhoneNumber = phoneValidationService.formatPhoneNumber;
  const cleanPhoneNumber = phoneValidationService.cleanPhoneNumber;

  return {
    validatePhoneNumbers,
    isPhoneValid,
    isPhoneInvalid,
    isLoading,
    formatPhoneNumber,
    cleanPhoneNumber,
    validationState
  };
};